import type { AppDependencies } from '@/appDependencies'
import { getSearchController, LIBRARY_FILTER_TYPE } from '@/search/searchState'
import { createDisposer, noop, type Unsubscribe } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { AuthStatus } from '@amzn/amp-playback'
import { SignInWall } from '@/auth/SignInWall'
import { back, getRouter, navigateTo, type RouteOptions } from '@/router/router'
import { startViewContextRouterFeed } from '@/router/viewContextRouterFeed'
import { startNowPlayingViewContextFeed } from '@/blackbolt/nowPlayingViewContextFeed'
import { getDeviceConfig, isTv } from '@/device/deviceConfig'
import { el } from '@amazon/vinyl-tsx'
import { bindBackKey } from '@/device/input/backKey'
import { bindFullscreenEscapeLock } from '@/fullscreen/escapeKeyLock'
import { NotFoundPage } from '@/components/NotFoundPage'
import { ErrorPage } from '@/error/ErrorPage'
import { ACTIVITY_FEED_ROUTE } from '@/profile/activityFeedRoute'
import { BROWSE_ALL_ROUTE, HOME_ROUTE, PLAY_ROUTE } from '@/router/homeRoutes'
import { kidsModeGuarded } from '@/kidsMode/kidsModeRouteGuard'
import { featureEnabled } from '@/core/featureGate'
import { openChatPane } from '@/blackbolt/chatPaneState'
import { isPodcastCategorySegment } from '@/router/podcastCategorySegment'
import { staticPage } from '@/staticRender'

// One `import()` literal per shared page: Vite emits a `__vite__mapDeps`
// preload array per literal, so a single literal avoids duplicating it per call site.
const loadSeeAll = () => import('@/seeAll/SeeAllPage')
const loadMyLibraryList = () => import('@/library/MyLibraryListPage')
const loadMyPlaylists = () => import('@/library/MyPlaylistsPage')
const loadBrowseHome = () => import('@/browse/BrowseHomePage')
const loadTopStations = () => import('@/stations/TopStationsPage')
const loadUserFollows = () => import('@/profile/UserFollowsPage')
const loadPlaylistDetail = () => import('@/playlistdetail/PlaylistDetailPage')
const loadMerchShop = () => import('@/merch/MerchShopPage')
const loadLibrary = () => import('@/library/Library')
const loadFeed = () => import('@/blackbolt/Feed')
const loadMusicHome = () => import('@/music/MusicHome')
const loadPodcasts = () => import('@/podcasts/Podcasts')
const loadAudiobooksHome = () => import('@/audiobook/AudiobooksHomePage')
const loadRoutePageViews = () => import('@/routePageViews')

/** The smart TV's now-playing controls: the mobile full player's, docked beside the stage. */
async function tvNowPlayingSidePanel(deps: AppDependencies): Promise<HTMLElement> {
    const [player, { FullPlayerPanel }] = await Promise.all([
        deps.playerPromise,
        import('@/transport/FullPlayerPanel'),
    ])
    return FullPlayerPanel({ deps: { ...deps, player } })
}

async function podcastStageTransport(deps: AppDependencies): Promise<HTMLElement> {
    const [player, { PodcastStageTransport }] = await Promise.all([
        deps.playerPromise,
        import('@/transport/PodcastStageTransport'),
    ])
    return PodcastStageTransport({ deps: { ...deps, player } })
}

export function registerRoutes(deps: AppDependencies): Unsubscribe {
    const router = getRouter()

    // Shared render helpers so `/`, `/feed`, and `/all` render the same surface
    // without duplicating the dynamic-import wiring. Chat is an overlay (mounted at
    // app root by AppView), not a route, so the feed is identical on every width.
    const renderFeed = async () => (await loadFeed()).Feed({ deps })
    const renderBrowseHome = async ({ showLeanBack = false } = {}) => {
        const { BrowseHome } = await import('@/BrowseHome')
        return BrowseHome({ deps, showLeanBack })
    }

    // `/playlists/...` and its `/user-playlists/...` alias share one factory;
    // the playlist id is namespaced server-side, so both render the same page.
    const playlistDetailRoute = (pattern: string): RouteOptions<['playlistId', 'mode']> => ({
        pattern,
        paramNames: ['playlistId', 'mode'],
        allowPreload: true,
        factory: async (props) => {
            return (await loadPlaylistDetail()).PlaylistDetailPage({
                playlistId: props.playlistId,
                deps,
                editMode: props.mode === 'edit',
            })
        },
    })

    // Maestro's just-composed bag lives only in memory; a stale/shared id (no
    // prior generation this session) resolves nothing and renders not-found.
    const generatedPlaylistRoute = (pattern: string): RouteOptions<['token']> => ({
        pattern,
        paramNames: ['token'],
        factory: async (props) => {
            const { getGeneratedBag } = await import('@/maestro/maestroService')
            const bag = getGeneratedBag(props.token)
            if (!bag) return NotFoundPage()
            return (await loadPlaylistDetail()).PlaylistDetailPage({
                playlistId: bag.id,
                deps,
                llmGenerated: bag,
            })
        },
    })

    // Before configure, whose initial navigation shows the first page; a static
    // render has no customer to report on. Metrics are best-effort, so a failed
    // chunk load is dropped.
    const stopPageViews = staticPage
        ? noop
        : router.on('pageShown', (event) => {
              const query = new URLSearchParams(location.search)
              loadRoutePageViews()
                  .then(({ reportRoutedPage }) => reportRoutedPage(event, query))
                  .catch(noop)
          })
    router.configure({
        stage: el('#stage'),
        // A signed-out session's browse/home queries 401 (anon credentials
        // aren't established in every browser), so don't eagerly warm routes
        // the user hasn't navigated to; navigation-time loads still run.
        canPreload: () => getAuth().isSignedIn(),
        routes: [
            {
                // Home renders the browse-all surface in place — URL stays `/`,
                // no redirect: the home button reads active only while the path
                // is `/` (see Header), so a redirect would leave it permanently
                // inactive. Same surface as `/all` for signed-in and anon.
                pattern: HOME_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: () => renderBrowseHome(),
            },
            {
                // Agent feed — the Ask-AI destination. Gated on the `amp` feature
                // flag: with the gate off the route resolves to NotFound, so the
                // page stays registered but disappears until re-enabled. Opening
                // it raises the chat sidebar over the feed, as if the input bar
                // was focused (chat is an overlay, not a route).
                pattern: '/feed',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    return renderFeed()
                },
            },
            {
                pattern: '/tastes',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await import('@/tastes/TastesPage')).TastesPage()
                },
            },
            {
                // Chat is an overlay, not a page: `/chat` (and its
                // `/chat/:sessionId` sibling) renders the feed underneath, then
                // opens the Chat Pane on the linked session or `?q=`/`?seed=` ingress.
                // Gated on the `amp` flag like `/feed` — with the gate off there's
                // no chat pane (or its DialogController bootstrap) to enter.
                pattern: '/chat',
                paramNames: [],
                factory: async () => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { enterChatPane } = await import('@/blackbolt/chat/chatPaneEntry')
                    enterChatPane()
                    return renderFeed()
                },
            },
            {
                pattern: '/chat/:sessionId',
                paramNames: ['sessionId'],
                factory: async (params) => {
                    if (!(await featureEnabled('amp'))) return NotFoundPage()
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { enterChatPane } = await import('@/blackbolt/chat/chatPaneEntry')
                    enterChatPane({ sessionId: params.sessionId })
                    return renderFeed()
                },
            },
            {
                pattern: BROWSE_ALL_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: () => renderBrowseHome(),
            },
            {
                pattern: '/videos',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { VideosPage } = await import('@/browse/VideosPage')
                    return VideosPage({ deps })
                }),
            },
            {
                pattern: '/video-podcasts',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { VideoPodcastsPage } = await import('@/browse/VideoPodcastsPage')
                    return VideoPodcastsPage({ deps })
                }),
            },
            {
                // Auth-only: an anonymous deep-link would strand the user on a
                // detail page whose load dead-ends with a CORS/auth error.
                pattern: '/videos/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { VideoDetailPage } = await import('@/video/VideoDetailPage')
                    return VideoDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/profile',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await import('@/profile/ProfilePage')).ProfilePage()
                },
            },
            {
                pattern: '/profiles',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ProfilesPage } = await import('@/profile/ProfilesPage')
                    return ProfilesPage()
                },
            },
            {
                pattern: '/identity/who-is-listening',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { SelectProfilePage } = await import('@/profile/SelectProfilePage')
                    const returnTo =
                        new URL(window.location.href).searchParams.get('returnTo') ?? undefined
                    return SelectProfilePage({ returnTo })
                },
            },
            {
                pattern: '/subscription',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SubscriptionPage } = await import('@/subscription/SubscriptionPage')
                    return SubscriptionPage()
                },
            },
            {
                pattern: '/manage-settings',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    return (await import('@/settings/SettingsPage')).SettingsPage({
                        streamingSettings: deps.streamingSettings,
                    })
                }),
            },
            ...['/streamingQualitySettings', '/displayPreferences'].map((pattern) => ({
                pattern,
                paramNames: [],
                factory: () => {
                    getRouter().navigateTo('/manage-settings', { replace: true })
                    return null
                },
            })),
            {
                pattern: '/help',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await import('@/help/HelpPage')).HelpPage()
                },
            },
            {
                // Linked from the avatar dropdown only when the debug flag is
                // set, but always registered so a direct URL works in prod.
                pattern: '/debug',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await import('@/debug/DebugControlsPage')).DebugControlsPage()
                },
            },
            {
                pattern: '/podcasts',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { Podcasts } = await loadPodcasts()
                    return Podcasts({ deps })
                }),
            },
            {
                // Top-charts grid: uses `topPodcastShows` directly, not a
                // recommender, so it's its own page.
                pattern: '/podcasts/top-charts',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { TopPodcastsPage } = await import('@/podcast/TopPodcastsPage')
                    return TopPodcastsPage({ deps })
                }),
            },
            // Redirect alias so old bookmarks land on the canonical /my/library.
            // `replace: true` + `return null` keeps `currentPath`/`bullfrogDepth`
            // in sync and avoids stacking a second back-stack entry (see the
            // `/recently/*` redirects below for the same idiom).
            {
                pattern: '/library',
                paramNames: [],
                factory: () => {
                    navigateTo('/my/library', { replace: true })
                    return null
                },
            },
            {
                // `?type=` rides the query string (read in `Search`, like `?q=`),
                // so it stays out of `paramNames` — the router tracks only path params.
                pattern: '/search',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { Search } = await import('@/search/Search')
                    return Search({ controller: getSearchController(), deps })
                },
            },
            {
                // Per-entity see-more grid. Query rides `?q=` (link-shareable,
                // like `/search`); the entity slug sits in the path.
                pattern: '/find/see-more/:type',
                paramNames: ['type'],
                allowPreload: true,
                factory: async (params) => {
                    const { SearchSeeMorePage } = await import('@/search/SearchSeeMorePage')
                    const search = new URL(window.location.href).searchParams
                    return SearchSeeMorePage({
                        type: params.type,
                        query: search.get('q') ?? '',
                        isLibraryScope: search.get('type') === LIBRARY_FILTER_TYPE,
                        deps,
                    })
                },
            },
            {
                // GH's find category-grouping see-more; amp serves the same grid
                // at the browse-recommendation path, so redirect inbound GH links there.
                pattern: '/find/groupings/:id',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(
                        `/browse/music-items/home/recommendation/${encodeURIComponent(params.id)}`,
                        { replace: true }
                    )
                    return null
                },
            },
            {
                pattern: '/albums/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { AlbumDetailPage } = await import('@/album/AlbumDetailPage')
                    return AlbumDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/tracks/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { TrackDetailPage } = await import('@/track/TrackDetailPage')
                    return TrackDetailPage({ id: params.id, deps })
                },
            },
            {
                // -artists and -playlists share the `recommendationCollection(type:
                // "track")` query; amp surfaces the full track-scoped stack.
                pattern: '/tracks/:id/related-artists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { BrowseHomePage } = await loadBrowseHome()
                    return BrowseHomePage({
                        deps,
                        type: 'track',
                        title: 'Related artists',
                        targets: [params.id],
                    })
                },
            },
            {
                pattern: '/tracks/:id/related-playlists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { BrowseHomePage } = await loadBrowseHome()
                    return BrowseHomePage({
                        deps,
                        type: 'track',
                        title: 'Related playlists',
                        targets: [params.id],
                    })
                },
            },
            {
                // No amp equivalent — related content lives on the track detail
                // page; redirect the dormant GH shape so shared links don't 404.
                pattern: '/tracks/:id/related',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(`/tracks/${encodeURIComponent(params.id)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                pattern: '/artists/:id/related-artists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: `uri:///artist/${params.id}/related-artists`,
                        deps,
                    })
                },
            },
            {
                pattern: '/artists/:id/all-access-songs',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistAllAccessSongsPage } =
                        await import('@/artist/ArtistAllAccessSongsPage')
                    return ArtistAllAccessSongsPage({ id: params.id, deps })
                },
            },
            // Dedicated artist see-all sub-routes — each has its own fetch shape,
            // so they can't collapse into the generic SeeAllPage. Registered
            // BEFORE `/artists/:id` so the specific segments aren't shadowed.
            {
                pattern: '/artists/:id/chronological-albums',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistChronologicalAlbumsPage } =
                        await import('@/artist/ArtistChronologicalAlbumsPage')
                    return ArtistChronologicalAlbumsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/popular-albums',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularAlbumsPage } =
                        await import('@/artist/ArtistPopularAlbumsPage')
                    return ArtistPopularAlbumsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/popular-songs',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularSongsPage } =
                        await import('@/artist/ArtistPopularSongsPage')
                    return ArtistPopularSongsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/popular-videos',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistPopularVideosPage } =
                        await import('@/artist/ArtistPopularVideosPage')
                    return ArtistPopularVideosPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/related-stations',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistRelatedStationsPage } =
                        await import('@/artist/ArtistRelatedStationsPage')
                    return ArtistRelatedStationsPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/artists/:id/related-podcasts',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { ArtistRelatedPodcastsPage } =
                        await import('@/artist/ArtistRelatedPodcastsPage')
                    return ArtistRelatedPodcastsPage({ id: params.id, deps })
                }),
            },
            {
                // GH's legacy alias: other clients mint the long form.
                pattern: '/artists/:id/related-podcasts-to-artist',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(
                        `/artists/${encodeURIComponent(params.id)}/related-podcasts`,
                        { replace: true }
                    )
                    return null
                },
            },
            {
                pattern: '/artists/:id/related-playlists',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistRelatedPlaylistsPage } =
                        await import('@/artist/ArtistRelatedPlaylistsPage')
                    return ArtistRelatedPlaylistsPage({ id: params.id, deps })
                },
            },
            {
                // Registered BEFORE `/artists/:id` so the `fan-poll` segment
                // isn't shadowed.
                pattern: '/artists/:id/fan-poll',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistFanPollPage } = await import('@/artist/ArtistFanPollPage')
                    return ArtistFanPollPage({ id: params.id })
                },
            },
            {
                // `:slug?` is SEO-only: the server emits a slugged canonical
                // `/artists/:id/:slug`, so a cold/bookmarked/shared load must
                // resolve here. Registered AFTER the specific
                // `/artists/:id/<segment>` routes above so ordering (=
                // specificity) lets those match first — the id is what resolves.
                pattern: '/artists/:id/:slug?',
                paramNames: ['id', 'slug'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistDetailPage } = await import('@/artist/ArtistDetailPage')
                    const promotionSlot =
                        new URL(window.location.href).searchParams.get('promotionSlot') ?? undefined
                    return ArtistDetailPage({ id: params.id, promotionSlot, deps })
                },
            },
            {
                // Category landing (`/podcasts/categories/<title>/dmp.category.<uuid>`,
                // matches green hornet's `[categoryTitle]/[categoryId]`).
                // Registered BEFORE `/podcasts/:categoryId/:strategyId` so its
                // two-segment regex doesn't capture `categories` + `<title>`.
                // `:categoryId?` stays optional for pre-existing name-only
                // bookmarks/links — the page falls back to a name lookup.
                pattern: '/podcasts/categories/:categoryTitle/:categoryId?',
                paramNames: ['categoryTitle', 'categoryId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PodcastCategoryPage } = await import('@/podcasts/PodcastCategoryPage')
                    return PodcastCategoryPage({
                        categoryTitle: params.categoryTitle,
                        categoryId: params.categoryId,
                        deps,
                    })
                }),
            },
            {
                // `showId` is constrained to a UUID so a slugged URL resolves
                // here without colliding with the sibling shapes
                // (`providers/:id`, `:categoryId/:strategyId`). `:slug?` is SEO-only.
                pattern: '/podcasts/:showId(UUID)/:slug?',
                paramNames: ['showId', 'slug'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PodcastShowDetailPage } =
                        await import('@/podcast/PodcastShowDetailPage')
                    return PodcastShowDetailPage({ id: params.showId, deps })
                }),
            },
            {
                // Podcast episode deep link. `:slug?` is SEO-only; both ids are
                // used — episode id fetches the payload, show id is required by
                // the `episode` recommender (targets [episodeId, showId]).
                pattern: '/podcasts/:showId/episodes/:episodeId/:slug?',
                paramNames: ['showId', 'episodeId', 'slug'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PodcastEpisodePage } = await import('@/podcast/PodcastEpisodePage')
                    return PodcastEpisodePage({
                        id: params.episodeId,
                        showId: params.showId,
                        deps,
                    })
                }),
            },
            {
                // Provider-scoped podcast landing. Registered BEFORE
                // `/podcasts/:categoryId/:strategyId` so `providers` isn't shadowed.
                pattern: '/podcasts/providers/:providerId',
                paramNames: ['providerId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PodcastProviderPage } = await import('@/podcasts/PodcastProviderPage')
                    return PodcastProviderPage({
                        providerId: params.providerId,
                        deps,
                    })
                }),
            },
            {
                pattern: '/podcasts/pages/:target',
                paramNames: ['target'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PodcastPagesPage } = await import('@/podcasts/PodcastPagesPage')
                    return PodcastPagesPage({ target: params.target, deps })
                }),
            },
            {
                pattern: '/podcasts/:categoryId/:strategyId',
                paramNames: ['categoryId', 'strategyId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    // Two shapes share this route: a category see-more carries a
                    // `dmp.category.<uuid>` first segment (curated-content query);
                    // a podcasts-home "See more" carries a strategy name (generic
                    // recommender). Route by whether the first segment embeds a UUID.
                    if (isPodcastCategorySegment(params.categoryId)) {
                        const { PodcastSeeMorePage } = await import('@/podcast/PodcastSeeMorePage')
                        return PodcastSeeMorePage({
                            categoryId: params.categoryId,
                            strategyId: params.strategyId,
                            deps,
                        })
                    }
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ widgetId: params.strategyId, deps })
                }),
            },
            {
                pattern: '/new',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { NewReleasesPage } = await import('@/newReleases/NewReleasesPage')
                    return NewReleasesPage({ deps })
                },
            },
            // /new/* see-all pages — flat see-all grids backed by fixed
            // `uri:///new/...` recommenders.
            {
                pattern: '/new/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///new/songs', deps })
                },
            },
            {
                pattern: '/new/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///new/albums', deps })
                },
            },
            {
                pattern: '/new/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///new/playlists', deps })
                },
            },
            {
                pattern: '/new/new-releases-for-you',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///new/new-releases-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/new/upcoming-releases',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///new/upcoming-releases', deps })
                },
            },
            {
                pattern: '/my/library',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { Library } = await loadLibrary()
                    return Library({ deps })
                },
            },
            {
                pattern: '/my/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyPlaylistsPage } = await loadMyPlaylists()
                    return MyPlaylistsPage({ deps, variant: 'all' })
                },
            },
            {
                pattern: '/my/playlists/by-me',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await loadMyPlaylists()
                    return MyPlaylistsPage({ deps, variant: 'by-me' })
                },
            },
            {
                pattern: '/my/playlists/followed',
                paramNames: [],
                factory: async () => {
                    const { MyPlaylistsPage } = await loadMyPlaylists()
                    return MyPlaylistsPage({ deps, variant: 'followed' })
                },
            },
            {
                pattern: '/my/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await loadMyLibraryList()
                    return MyLibraryListPage({ deps, kind: 'albums' })
                },
            },
            {
                pattern: '/my/artists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await loadMyLibraryList()
                    return MyLibraryListPage({ deps, kind: 'artists' })
                },
            },
            {
                pattern: '/my/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await loadMyLibraryList()
                    return MyLibraryListPage({ deps, kind: 'songs' })
                },
            },
            {
                pattern: '/my/podcasts/playlist',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { MyPodcastPlaylistPage } =
                        await import('@/library/MyPodcastPlaylistPage')
                    return MyPodcastPlaylistPage({ deps })
                }),
            },
            {
                // Newest episodes across followed shows — the default podcasts tab.
                pattern: '/my/podcasts/latest',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { LatestPodcastsPage } = await import('@/library/LatestPodcastsPage')
                    return LatestPodcastsPage({ deps })
                }),
            },
            {
                // Followed shows grid (`followedPodcastShows` as a card grid).
                pattern: '/my/podcasts/shows',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { MyLibraryListPage } = await loadMyLibraryList()
                    return MyLibraryListPage({ deps, kind: 'podcasts' })
                }),
            },
            {
                // Bare `/my/podcasts` → Latest Episodes. Replace so back-nav
                // skips the bare path.
                pattern: '/my/podcasts',
                paramNames: [],
                factory: kidsModeGuarded(() => {
                    getRouter().navigateTo('/my/podcasts/latest', { replace: true })
                    return null
                }),
            },
            {
                pattern: '/my/purchases',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MyLibraryListPage } = await loadMyLibraryList()
                    return MyLibraryListPage({ deps, kind: 'purchased' })
                },
            },
            {
                // Continue Listening, Saved Audiobooks, and Previous borrows.
                pattern: '/my/audiobooks',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { MyAudiobooksPage } = await import('@/library/MyAudiobooksPage')
                    return MyAudiobooksPage({ deps })
                }),
            },
            {
                pattern: '/music',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    return (await loadMusicHome()).MusicHome({ deps })
                },
            },
            {
                pattern: '/browse/music-items/home/recommendation/:widgetId',
                paramNames: ['widgetId'],
                allowPreload: true,
                factory: async (params) => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ widgetId: params.widgetId, deps })
                },
            },
            // Charts hub — stacks the four popular-chart shovelers, each linking
            // to its dedicated see-all page below.
            {
                pattern: '/charts',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ChartsHubPage } = await import('@/browse/ChartsHubPage')
                    return ChartsHubPage({ deps })
                },
            },
            {
                // GH serves its charts hub at /popular; /charts stays canonical.
                pattern: '/popular',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ChartsHubPage } = await import('@/browse/ChartsHubPage')
                    return ChartsHubPage({ deps })
                },
            },
            // /popular/* — flat see-all grids backed by fixed `uri:///popular/...`
            // recommenders, passed to SeeAllPage as recommenderId.
            {
                pattern: '/popular/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-songs', deps })
                },
            },
            {
                pattern: '/popular/albums',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-albums', deps })
                },
            },
            {
                pattern: '/popular/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///popular/top-playlists', deps })
                },
            },
            {
                pattern: '/popular/trending-songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({ recommenderId: 'uri:///popular/trending-songs', deps })
                },
            },
            {
                // Personalized Playlists hub — same recommender shape as /popular/*.
                pattern: '/my/playlists/personalized',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/playlists-for-you',
                        deps,
                    })
                },
            },
            {
                // Recently-updated playlists (GH's RecentlyUpdated page).
                pattern: '/my/playlists/recently-updated',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/recently-updated',
                        deps,
                    })
                },
            },
            {
                // `topStations` isn't a recommender, so it can't use SeeAllPage's
                // `recommender(id:)` wrapper — mirror it as a dedicated page.
                pattern: '/popular/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { TopStationsPage } = await loadTopStations()
                    return TopStationsPage({ deps })
                },
            },
            {
                // Stations landing hub — stacks Stations for You, Recently Played,
                // and Top Stations, each linking to its see-all page.
                pattern: '/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { StationsPage } = await import('@/stations/StationsPage')
                    return StationsPage({ deps })
                },
            },
            {
                // Top Stations see-all, reachable from the Stations hub's URL
                // space (mirrors `/popular/stations`).
                pattern: '/stations/top',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { TopStationsPage } = await loadTopStations()
                    return TopStationsPage({ deps })
                },
            },
            {
                // Stations for You see-all. No `stations-for-you` recommender
                // exists, so it reads from `user.topStations` like the hub row.
                pattern: '/stations/for-you',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { StationsForYouPage } = await import('@/stations/StationsForYouPage')
                    return StationsForYouPage({ deps })
                },
            },
            {
                // Popular Artists hub — same recommender shape as /popular/*.
                pattern: '/artists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///popular/top-artists',
                        deps,
                    })
                },
            },
            // Playlist see-all pages — same recommender shape as /popular/*.
            {
                pattern: '/playlists/playlists-for-you',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/playlists-for-you',
                        deps,
                    })
                },
            },
            {
                pattern: '/playlists/recently-updated-playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: 'uri:///playlists/recently-updated-playlists',
                        deps,
                    })
                },
            },
            {
                pattern: '/recently/added',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { RecentlyAdded } = await import('@/recentlyAdded/RecentlyAdded')
                    return RecentlyAdded({ deps })
                },
            },
            {
                pattern: '/recently/played/songs',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { SongHistoryPage } = await import('@/library/SongHistoryPage')
                    return SongHistoryPage({ deps })
                },
            },
            {
                pattern: '/recently/played/stations',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { RecentlyPlayedStationsPage } =
                        await import('@/stations/RecentlyPlayedStationsPage')
                    return RecentlyPlayedStationsPage({ deps })
                },
            },
            // `/recently/{purchased,played}` redirect into `/my/...`. Route the
            // rewrite THROUGH the router (`replace: true`) so `currentPath` and the
            // `bullfrogDepth` tag stay in sync — a raw `replaceState` desynced both.
            // The re-nav re-renders, so these factories return null.
            {
                pattern: '/recently/purchased',
                paramNames: [],
                factory: () => {
                    getRouter().navigateTo('/my/purchases', { replace: true })
                    return null
                },
            },
            {
                // Target `/my/library` directly, not `/library`: `/library` is
                // itself a push-redirect, so replacing into it would add a phantom
                // back-stack entry.
                pattern: '/recently/played',
                paramNames: [],
                factory: () => {
                    getRouter().navigateTo('/my/library', { replace: true })
                    return null
                },
            },
            // Parity stubs for entity types amp doesn't fully render yet, so
            // deep links don't 404 while the underlying pieces ship.
            {
                pattern: '/maestro',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MaestroPage } = await import('@/maestro/MaestroPage')
                    return MaestroPage({ deps })
                },
            },
            {
                pattern: '/live',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { LiveEventsPage } = await import('@/liveevent/LiveEventsPage')
                    return LiveEventsPage()
                }),
            },
            {
                pattern: '/events/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { LiveEventDetailPage } = await import('@/liveevent/LiveEventDetailPage')
                    return LiveEventDetailPage({ id: params.id, deps })
                }),
            },
            {
                pattern: '/live/events/:id',
                paramNames: ['id'],
                factory: (params) => {
                    getRouter().navigateTo(`/events/${encodeURIComponent(params.id)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                // amp folds the lineup into event detail; redirect the GH
                // lineup shape so shared links resolve instead of 404ing.
                pattern: '/live/events/:eventId/lineup',
                paramNames: ['eventId'],
                factory: (params) => {
                    getRouter().navigateTo(`/events/${encodeURIComponent(params.eventId)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            {
                // AMP live-event detail, reached from `LiveEventCard`'s fallback
                // CTA. Distinct from `/events/:id` so the AMP surface can iterate
                // without regressing the shared detail page.
                pattern: '/liveEvents/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { LiveEventDetailPage } =
                        await import('@/blackbolt/liveEvent/LiveEventDetailPage')
                    return LiveEventDetailPage({ id: params.id })
                }),
            },
            {
                // Nearby concerts hub — Firefly's `nearbyConcerts` (default
                // 100mi radius, upcoming-only).
                pattern: '/concerts',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { ConcertsPage } = await import('@/concert/ConcertsPage')
                    return ConcertsPage()
                },
            },
            {
                pattern: '/concerts/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { ConcertDetailPage } = await import('@/concert/ConcertDetailPage')
                    return ConcertDetailPage({ id: params.id })
                }),
            },
            {
                pattern: '/merch',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MerchShopPage } = await loadMerchShop()
                    return MerchShopPage({})
                },
            },
            {
                // Category-scoped merch storefront. `?tag`/`?category`/`?sequence`
                // ride the query string (like `/search`'s `?q=`), so they stay out
                // of paramNames.
                pattern: '/merch/shop',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { MerchShopPage } = await loadMerchShop()
                    const params = new URL(window.location.href).searchParams
                    const categoryIds = [
                        params.get('tag'),
                        params.get('category'),
                        params.get('sequence'),
                    ].filter((v): v is string => !!v)
                    return MerchShopPage(categoryIds.length > 0 ? { categoryIds } : {})
                },
            },
            {
                pattern: '/merch/artists/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { ArtistMerchPage } = await import('@/merch/ArtistMerchPage')
                    return ArtistMerchPage({ id: params.id })
                },
            },
            {
                // Merch item detail route — `/merch/items/:id` (GH parity).
                // `?artistId=` rides the query string (like `/merch/shop`'s
                // `?tag=`) so a tile reached through an artist surface drives the
                // artist-information block below; `?ref=` is forwarded to the shop.
                pattern: '/merch/items/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { MerchDetailPage } = await import('@/merch/MerchDetailPage')
                    const query = new URL(window.location.href).searchParams
                    return MerchDetailPage({
                        id: params.id,
                        artistId: query.get('artistId') ?? undefined,
                        incomingRef: query.get('ref') ?? undefined,
                    })
                },
            },
            {
                pattern: '/genres/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { GenreDetailPage } = await import('@/genre/GenreDetailPage')
                    return GenreDetailPage({ id: params.id, deps })
                },
            },
            {
                pattern: '/curated/:id',
                paramNames: ['id'],
                allowPreload: true,
                factory: async (params) => {
                    const { CuratedTopicPage } = await import('@/curated/CuratedTopicPage')
                    return CuratedTopicPage({ id: params.id, deps })
                },
            },
            {
                // See-all for one of a genre page's shovelers. Green hornet
                // targets the recommender via `uri:///category/<id>/<widgetId>`.
                pattern: '/genres/:id/:recommenderId',
                paramNames: ['id', 'recommenderId'],
                allowPreload: true,
                factory: async (params) => {
                    const { SeeAllPage } = await loadSeeAll()
                    return SeeAllPage({
                        recommenderId: `uri:///category/${params.id}/${params.recommenderId}`,
                        deps,
                    })
                },
            },
            {
                pattern: '/audiobooks/library',
                paramNames: [],
                factory: kidsModeGuarded(() => {
                    // Route through the router (`replace: true`) to keep
                    // `currentPath` + `bullfrogDepth` in sync; re-nav renders the page.
                    getRouter().navigateTo('/my/audiobooks', { replace: true })
                    return null
                }),
            },
            {
                // Curated audiobook browse home. Audiobook rows route to
                // `/audiobooks/:id`; category tiles to `/audiobooks/categories/:id`.
                pattern: '/audiobooks',
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    const { AudiobooksHomePage } = await loadAudiobooksHome()
                    return AudiobooksHomePage({ deps })
                }),
            },
            {
                // Audiobook category browse — categoryId is the
                // recommendation-collection id; the page renders it as a
                // BrowseHomePage stack headed by the category's real name.
                pattern: '/audiobooks/categories/:categoryId',
                paramNames: ['categoryId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { AudiobookCategoryPage } =
                        await import('@/audiobook/AudiobookCategoryPage')
                    return AudiobookCategoryPage({ id: params.categoryId, deps })
                }),
            },
            {
                // Owned/in-library titles get an in-app Play via the Audible
                // sub-player, so the page needs playback deps.
                pattern: '/audiobooks/:audiobookId',
                paramNames: ['audiobookId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (props) => {
                    const { AudiobookDetailPage } = await import('@/audiobook/AudiobookDetailPage')
                    return AudiobookDetailPage({ id: props.audiobookId, deps })
                }),
            },
            {
                pattern: '/now-playing',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const [{ NowPlayingPage }, sidePanel, podcastTransport] = await Promise.all([
                        import('@/nowPlaying/NowPlayingPage'),
                        isTv() ? tvNowPlayingSidePanel(deps) : undefined,
                        podcastStageTransport(deps),
                    ])
                    return NowPlayingPage({
                        deps,
                        podcastTransport,
                        ...(sidePanel ? { sidePanel } : null),
                    })
                },
            },
            {
                // Registered BEFORE `/playlists/:id` so the invite landing
                // isn't shadowed by the detail page.
                pattern: '/playlists/invites/:inviteId',
                paramNames: ['inviteId'],
                allowPreload: true,
                factory: async (params) => {
                    const { PlaylistInvitePage } =
                        await import('@/playlistdetail/PlaylistInvitePage')
                    return PlaylistInvitePage({ inviteId: params.inviteId })
                },
            },
            {
                // Viewer's own activity feed — auth-gated since it reads
                // `user.activity`, so anonymous sessions hit the sign-in wall.
                pattern: ACTIVITY_FEED_ROUTE,
                paramNames: [],
                allowPreload: true,
                factory: kidsModeGuarded(async () => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { ActivityFeedPage } = await import('@/profile/ActivityFeedPage')
                    return ActivityFeedPage()
                }),
            },
            {
                pattern: '/users/:userId/playlists',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { PublicPlaylistsPage } =
                        await import('@/playlistdetail/PublicPlaylistsPage')
                    return PublicPlaylistsPage({ userId: params.userId, deps })
                }),
            },
            {
                pattern: '/users/:userId/followers',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { UserFollowsPage } = await loadUserFollows()
                    return UserFollowsPage({ userId: params.userId, variant: 'followers' })
                }),
            },
            {
                pattern: '/users/:userId/following',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { UserFollowsPage } = await loadUserFollows()
                    return UserFollowsPage({ userId: params.userId, variant: 'following' })
                }),
            },
            {
                // Registered AFTER the `/users/:userId/{playlists,followers,
                // following}` sub-routes so its regex doesn't shadow them (the
                // router walks routes in registration order).
                pattern: '/users/:userId',
                paramNames: ['userId'],
                allowPreload: true,
                factory: kidsModeGuarded(async (params) => {
                    const { ProfileDetailPage } = await import('@/profile/ProfileDetailPage')
                    return ProfileDetailPage({ userId: params.userId })
                }),
            },
            {
                pattern: '/playlists',
                paramNames: [],
                allowPreload: true,
                factory: async () => {
                    const { PlaylistsHubPage } = await import('@/browse/PlaylistsHubPage')
                    return PlaylistsHubPage({ deps })
                },
            },
            {
                // The slug maps to an opaque Firefly playlist-set enum that
                // `/playlists/:id` can't resolve, so resolve it to the viewer's
                // real id and redirect. Registered BEFORE `/playlists/:playlistId`
                // so `pp` isn't captured as an id. Auth-gated (viewer's own).
                pattern: '/playlists/pp/:slug',
                paramNames: ['slug'],
                factory: async (params) => {
                    if (!getAuth().isSignedIn()) return SignInWall()
                    const { resolvePersonalizedPlaylistId } =
                        await import('@/playlistdetail/personalizedPlaylistResolver')
                    const playlistId = await resolvePersonalizedPlaylistId(params.slug)
                    if (!playlistId) return NotFoundPage()
                    getRouter().navigateTo(`/playlists/${encodeURIComponent(playlistId)}`, {
                        replace: true,
                    })
                    return null
                },
            },
            // Maestro's just-composed generative playlist — not yet a persisted
            // playlist, so it renders from the in-memory bag rather than
            // `playlist(id:)`, with a save-to-public control. Registered BEFORE
            // the generic aliases so `llm-generated` isn't captured as an id.
            generatedPlaylistRoute('/playlists/llm-generated/:token'),
            generatedPlaylistRoute('/user-playlists/llm-generated/:token'),
            playlistDetailRoute('/playlists/:playlistId/:mode?'),
            // Alias for the "Listen on Amazon Music" share URL Hornet emits.
            playlistDetailRoute('/user-playlists/:playlistId/:mode?'),
            {
                // Voice / deep-link entry (`amp:///play?q=`, iOS Siri "Play X on
                // Amp"). Fires search-and-play, then redirects home so the URL
                // bar doesn't rest on a transient action path.
                pattern: PLAY_ROUTE,
                paramNames: [],
                factory: async () => {
                    const query = new URL(window.location.href).searchParams.get('q') ?? ''
                    if (query) {
                        const { playSearch } = await import('@/voice/playSearch')
                        playSearch(query, {
                            cloudQueueController: deps.cloudQueueController,
                        }).catch(() => {})
                    }
                    const { Feed } = await loadFeed()
                    // Raw `replaceState`, deliberately NOT routed through the
                    // router: here the URL target (`/`) differs from what we
                    // render (`Feed`), and routing would fire the `/` redirect
                    // chain and swap the surface. `/play` is an external-only,
                    // transient depth-0 entry, so the `currentPath` desync is benign.
                    history.replaceState(null, '', HOME_ROUTE)
                    return Feed({ deps })
                },
            },
        ],
        fallback: NotFoundPage,
        errorFallback: ErrorPage,
    })

    return () => {
        stopPageViews()
        router.clearRoutes()
    }
}

/**
 * Reloads the view route when the auth status changes to/from signed in <--> signed out
 */
export function initializeRouter(): Unsubscribe {
    const { add, dispose } = createDisposer()
    const router = getRouter()
    const auth = getAuth()

    // Feed the agent view-context provider's per-route baseline from the router
    // (one-way router → viewContext injection; the provider never imports here).
    add(startViewContextRouterFeed())
    // Feed the same provider's now-playing slice from the shared player so the
    // agent knows the current track on any route (blackbolt → viewContext).
    add(startNowPlayingViewContextFeed())

    // Raise the chat pane on real navigation to /feed — on `navigate`, not the
    // route factory, so a hover/idle preload build never opens it and a re-nav
    // to the memoized /feed still does. Gate + sign-in mirror the factory so a
    // gated-off / anon session (NotFound / SignInWall) doesn't raise chat.
    add(
        router.on('navigate', ({ current }) => {
            if (current !== '/feed') return
            featureEnabled('amp')
                .then((enabled) => {
                    if (enabled && auth.isSignedIn()) openChatPane()
                })
                .catch(noop)
        })
    )

    router.on('navigating', (event) => {
        // Do not show pages when not logged in on TV.
        if (isTv() && !auth.isSignedIn()) {
            event.preventDefault()
        }

        // Block navigation while signing in; a reload fires when it completes.
        if (auth.status.value === AuthStatus.PENDING) {
            event.preventDefault()
        }
    })

    add(
        auth.status.onData((value, previous) => {
            if (previous && value !== AuthStatus.PENDING) {
                router.hardReload()
            }
        })
    )

    add(bindFullscreenEscapeLock())
    add(bindBackKey(getDeviceConfig().keyMap, back))

    return dispose
}
